// The two Seller Sourcing History Swipers, for the Seller pages (/seller/providers, /seller/dropshipping-in):
//   <RecentlyViewedProvidersSwiper />         "Shops You Follow" card, Provider data, 2x2 products (max 15)
//   <RecentlyViewedProviderProductsSwiper />  "Best Sellers"-inspired Provider Product cards (max 30)
// Same cards as the full pages (ProviderShowcaseCard, ProviderProductCard variant="history").
// Seller-capable effective roles only; skeletons while loading; a discreet Retry line on error; hidden
// when the history is empty (the full pages carry the empty states).
import React from "react";
import { FaStore, FaShoppingBag } from "react-icons/fa";
import { elakayAutoText as text } from "../../pageInside/dataElakay";
import ProviderShowcaseCard, { ProviderShowcaseSkeleton } from "../ui/ProviderShowcaseCard";
import ProviderProductCard, { ProviderProductCardSkeleton } from "../components/ProviderProductCard";
import HistorySwiperSection, { HistoryErrorSection, HistorySkeletonSection } from "./HistorySwiperSection";
import { SELLER_HISTORY_ROUTES, SELLER_HISTORY_MAX } from "./sellerHistoryRoutes";
import { canUseSellerSourcingHistory, historyProducts, useSellerSourcingHistory } from "./useSellerSourcingHistory";
import { useProviderLikeToggle } from "./useProviderLikeToggle";
import "../ui/ui.css";
import "./sellerHistory.css";

// "Shops You Follow" proportions: big cards so the 2x2 product images stay large -- 1.1 on phones
// (the next card peeks), 1.5 small tablets, 2 tablets/laptops, 3 on wide desktops.
export const PROVIDER_BREAKPOINTS = { 568: { slidesPerView: 1.5 }, 768: { slidesPerView: 2 }, 1400: { slidesPerView: 3 } };
// Product cards: the Best Sellers rhythm (2 / 3 / 4 / 5 per view), 1.6 on the smallest phones.
export const PRODUCT_BREAKPOINTS = { 480: { slidesPerView: 2.2 }, 640: { slidesPerView: 3 }, 992: { slidesPerView: 4 }, 1300: { slidesPerView: 5 } };

export function RecentlyViewedProvidersSwiper({ infoSource }) {
  const enabled = canUseSellerSourcingHistory(infoSource);
  const { items, loading, error, reload, setItems } = useSellerSourcingHistory("providers", { enabled, limit: SELLER_HISTORY_MAX.providers });
  const like = useProviderLikeToggle(setItems);
  const frame = { id: "providers", icon: FaStore, title: text("h_sph_providers_title", "Recently Viewed Providers"), subtitle: text("h_sph_providers_swiper_sub", "Providers you recently visited") };
  if (!enabled) return null;
  if (error) return <HistoryErrorSection {...frame} onRetry={reload} />;
  if (loading && !items.length) return <HistorySkeletonSection {...frame} Skeleton={ProviderShowcaseSkeleton} count={3} />;
  if (!items.length) return null;
  return (
    <HistorySwiperSection {...frame} viewAllTo={SELLER_HISTORY_ROUTES.providers} items={items} slidesPerView={1.1} breakpoints={PROVIDER_BREAKPOINTS}
      getKey={(entry) => String(entry.providerId)}
      renderItem={(entry) => <ProviderShowcaseCard entry={entry} onToggleLike={like.toggle} liking={like.isBusy(entry)} />} />
  );
}

export function RecentlyViewedProviderProductsSwiper({ infoSource }) {
  const enabled = canUseSellerSourcingHistory(infoSource);
  const { items, loading, error, reload } = useSellerSourcingHistory("products", { enabled, limit: SELLER_HISTORY_MAX.products });
  const frame = { id: "products", icon: FaShoppingBag, title: text("h_sph_products_title", "Recently Viewed Provider Products"), subtitle: text("h_sph_products_swiper_sub", "Products you recently viewed from providers") };
  if (!enabled) return null;
  if (error) return <HistoryErrorSection {...frame} onRetry={reload} />;
  if (loading && !items.length) return <HistorySkeletonSection {...frame} Skeleton={ProviderProductCardSkeleton} count={5} />;
  const products = historyProducts(items);
  if (!products.length) return null;
  return (
    <HistorySwiperSection {...frame} viewAllTo={SELLER_HISTORY_ROUTES.products} items={products} slidesPerView={1.6} breakpoints={PRODUCT_BREAKPOINTS}
      getKey={(p) => String(p._id)}
      renderItem={(p) => <ProviderProductCard variant="history" product={p} />} />
  );
}

// Both blocks, in the order of the ticket (Providers, then Provider Products).
export default function SellerSourcingHistorySection({ infoSource }) {
  if (!canUseSellerSourcingHistory(infoSource)) return null;
  return (
    <div className="sph-section">
      <RecentlyViewedProvidersSwiper infoSource={infoSource} />
      <RecentlyViewedProviderProductsSwiper infoSource={infoSource} />
    </div>
  );
}
